import { formatMonth } from "libs/organisms/src/lib/information-card/helpers";
import { formatHumanDate } from '@flexo/general';

/**
 * Interface representing an invoice from the real data source
 */
export interface Invoice {
  /** Unique invoice number */
  invoice_no: string;
  
  /** Start date of the billing period (YYYY-MM-DD) */
  period_start_date: string;
  
  /** End date of the billing period (YYYY-MM-DD) */
  period_end_date: string;
  
  /** The invoice amount */
  amount: number;
  
  /** Type of invoice (e.g., 'energy', 'service') */
  invoice_type: string;
  
  /** Date when the invoice was issued (YYYY-MM-DD) */
  emission_date: string;
  
  /** Date when the invoice is due (YYYY-MM-DD) */
  due_date: string;
  
  /** Whether the invoice has been paid */
  is_paid: boolean;
  
  /** Date when the invoice was paid, or null if not paid (YYYY-MM-DD | null) */
  payment_date: string | null;
  
  /** ID of the site to which this invoice belongs */
  siteId: string;
  
  /** ID of the user associated with this invoice */
  userId: string;
  
  /** URL to the invoice PDF document */
  url: string;
  
  /** Period identifier for the invoice (e.g., '2025_Q2') */
  invoice_period: string;
}

/**
 * Interface for the transformed structure that matches the 'data' format
 */
export interface TransformedData {
  /** Month identifier in YYYY-MM format */
  month: string;
  
  /** Array of site-specific invoice information */
  sites: Array<{
    /** Name of the site */
    siteName: string;
    
    /** Point of delivery identifier */
    pod: string;
    
    /** Invoice identifier */
    invoice: string;
    
    /** Invoice amount */
    amount: number;
    
    /** Payment status */
    paid: boolean;
    
    /** Date when the invoice was issued */
    invoiceDate: string;
    
    /** Invoice ID for reference */
    invoiceID: string;
    
    /** Due date for the invoice */
    invoiceDueDate: string;
    
    /** Currency unit */
    unit: string;
    
    /** Original invoice data preserved */
    [key: string]: any;
  }>;
}


/**
 * Transforms realDataSample data into the same structure as the data array
 * Groups invoices by month based on invoice_period and formats appropriately
 * 
 * @param realData - The raw invoice data from realDataSample
 * @returns Data structured in the same format as the 'data' variable
 */
export function transformRealDataToStructuredData(realData: Invoice[], metadata: any): TransformedData[] {
  // Group invoices by their invoice_period
  const groupedByPeriod: Record<string, Invoice[]> = {};

  // First group all items by invoice_period
  realData.forEach(invoice => {
    const period = invoice.invoice_period;
    if (!groupedByPeriod[period]) {
      groupedByPeriod[period] = [];
    }
    groupedByPeriod[period].push(invoice);
  });

  // Transform the grouped data to match the structure of 'data'
  const transformedData = Object.entries(groupedByPeriod).map(([period, invoices]) => {
    // Build month label from period_start_date and period_end_date
    const firstInvoice = invoices?.[0];
    const startDate = firstInvoice?.period_start_date;
    const endDate = firstInvoice?.period_end_date;
    let month = '';

    if (startDate && endDate) {
      month = `${formatHumanDate(startDate)} - ${formatHumanDate(endDate)}`;
    } else {
      // Fallback to previous behavior when dates are missing
      if (period.includes('Q')) {
        const [year, quarter] = period.split('_');
        const quarterToMonth = {
          Q1: '01',
          Q2: '04',
          Q3: '07',
          Q4: '10',
        } as const;
        month = formatMonth([new Date(`${year}-${quarterToMonth[quarter as keyof typeof quarterToMonth]}-01`), new Date()]);
      } else {
        month = period;
      }
    }

    // Transform each invoice to match the 'sites' structure
    const sites = invoices.map((invoice: Invoice) => {
      return {
        siteName: invoice.siteId, 
        pod: invoice.siteId, 
        invoice: invoice.invoice_no,
        paid: invoice.is_paid,
        invoiceDate: invoice.emission_date,
        invoiceID: invoice.invoice_no,
        invoiceDueDate: invoice.due_date,
        unit: 'CHF',
        ...invoice,
        ...(metadata?.entities?.sites || []).find((site: any) => site.siteId === invoice.siteId),
      };
    });

    return {
      month,
      sites,
      originalPeriod: period
    };
  });

  return transformedData.sort((a, b) => {
    const getPeriodValue = (period: string) => {
      if (period.includes('Q')) {
        const [year, quarter] = period.split('_');
        const quarterNum = parseInt(quarter.replace('Q', ''));
        return parseInt(year) * 10 + quarterNum;
      }
      return parseInt(period.replace(/\D/g, ''));
    };
    
    const valueA = getPeriodValue(a.originalPeriod);
    const valueB = getPeriodValue(b.originalPeriod);
    
    return valueB - valueA;
  }).map(({ month, sites }) => ({ month, sites })); 
}

/**
 * Sort sites within each month by the specified column
 * @param storedRows - The current stored rows data
 * @param columnId - The column ID to sort by
 * @param rowsOrder - Current sort order [columnId, direction]
 * @param setRowsOrder - Function to update the sort order state
 * @returns The sorted data array
 */
export function sortRows(storedRows: TransformedData[], columnId: string, rowsOrder: [string, string], setRowsOrder: (order: [string, string]) => void): TransformedData[] {
  try {
    // Create a deep copy of the stored rows
    let _midArray = storedRows.map((month) => ({
      ...month,
      sites: [...month.sites],
    }));

    // Determine sort direction (toggle if same column clicked)
    let direction = 'asc';
    if (rowsOrder[0] === columnId && rowsOrder[1] === 'asc') {
      direction = 'desc';
    }

    // Sort sites within each month
    _midArray = _midArray.map((month) => {
      const sortedSites = [...month.sites];

      switch (columnId) {
        case 'site':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? a.siteName > b.siteName
                : a.siteName < b.siteName
            )
              ? 1
              : -1
          );
          break;
        case 'resident':
          sortedSites.sort((a, b) =>
            (direction === 'asc' ? `${a.memberFirstName} ${a.memberLastName}` > `${b.memberFirstName} ${b.memberLastName}` : `${a.memberFirstName} ${a.memberLastName}` < `${b.memberFirstName} ${b.memberLastName}`) ? 1 : -1
          );
          break;
        case 'pod':
          sortedSites.sort((a, b) =>
            (direction === 'asc' ? a.pod > b.pod : a.pod < b.pod) ? 1 : -1
          );
          break;
        case 'invoice':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? a.invoiceID > b.invoiceID
                : a.invoiceID < b.invoiceID
            )
              ? 1
              : -1
          );
          break;
        case 'amount':
          sortedSites.sort((a, b) =>
            (direction === 'asc' ? a.amount > b.amount : a.amount < b.amount)
              ? 1
              : -1
          );
          break;
        case 'invoiceDate':
          sortedSites.sort((a, b) => {
            const d1 = new Date(a.invoiceDate);
            const d2 = new Date(b.invoiceDate);
            return (direction === 'asc' ? d1 > d2 : d1 < d2) ? 1 : -1;
          });
          break;
        case 'invoiceDueDate':
          sortedSites.sort((a, b) => {
            const d1 = new Date(a.invoiceDueDate);
            const d2 = new Date(b.invoiceDueDate);
            return (direction === 'asc' ? d1 > d2 : d1 < d2) ? 1 : -1;
          });
          break;
        case 'paid':
          sortedSites.sort((a, b) =>
            (
              direction === 'asc'
                ? Number(a.paid) > Number(b.paid)
                : Number(a.paid) < Number(b.paid)
            )
              ? 1
              : -1
          );
          break;
      }

      // Return the month object with sorted sites
      return {
        ...month,
        sites: sortedSites,
      };
    });

    // Update state with new sort order and sorted data
    setRowsOrder([columnId, direction]);
    return _midArray;
  } catch (err: any) {
    console.warn(err);
    return storedRows;
  }
}